import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FadeUp, Eyebrow } from "./Reveal";
import { API, CONTACT } from "@/lib/site";

const INITIAL = { name: "", phone: "", email: "", property_type: "", sqft: "", budget: "", message: "" };

const inputCls =
  "w-full bg-cream/70 border border-hairline focus:border-brass outline-none px-4 py-3.5 text-sm text-ink placeholder:text-taupe/70 transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.notified === false) {
        toast.message("Enquiry received", {
          description: "Saved — email delivery is momentarily unavailable, we'll still respond shortly.",
        });
      } else if (data.client_notified) {
        toast.success("Enquiry received — a confirmation is on its way to your inbox. The Studio31 will reach out shortly.");
      } else {
        toast.success("Enquiry received — The Studio31 will reach out to you shortly.");
      }
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry — please call or email us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <Eyebrow>Contact</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05]">
            Tell us about your space
          </h2>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-4 mt-14">
          <FadeUp className="lg:col-span-5" delay={0.06}>
            <div className="bg-ink text-cream p-9 lg:p-12 h-full flex flex-col justify-between gap-12">
              <div>
                <p className="font-serif italic text-xl text-cream/80 leading-snug max-w-sm">
                  Every project begins with a complimentary, personalized consultation.
                </p>
                <div className="mt-10 space-y-7">
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
                    <span className="h-10 w-10 border border-brass/40 flex items-center justify-center shrink-0 group-hover:bg-brass/10 transition-colors">
                      <Mail size={16} className="text-brass" />
                    </span>
                    <span>
                      <span className="block text-[10px] tracking-widest2 uppercase text-cream/45">Email</span>
                      <span className="block text-sm mt-1.5 group-hover:text-brass transition-colors break-all">{CONTACT.email}</span>
                    </span>
                  </a>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-start gap-4 group">
                    <span className="h-10 w-10 border border-brass/40 flex items-center justify-center shrink-0 group-hover:bg-brass/10 transition-colors">
                      <Phone size={16} className="text-brass" />
                    </span>
                    <span>
                      <span className="block text-[10px] tracking-widest2 uppercase text-cream/45">Phone</span>
                      <span className="block text-sm mt-1.5 group-hover:text-brass transition-colors">{CONTACT.phoneDisplay}</span>
                    </span>
                  </a>
                  <div data-testid="contact-address-row" className="flex items-start gap-4">
                    <span className="h-10 w-10 border border-brass/40 flex items-center justify-center shrink-0">
                      <MapPin size={16} className="text-brass" />
                    </span>
                    <span>
                      <span className="block text-[10px] tracking-widest2 uppercase text-cream/45">Studio</span>
                      <span className="block text-sm mt-1.5">{CONTACT.address}</span>
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-[11px] tracking-wider text-cream/35 uppercase">
                MSME Certified · UDYAM-UP-28-0029913
              </p>
            </div>
          </FadeUp>

          <FadeUp className="lg:col-span-7" delay={0.12}>
            <form data-testid="contact-inquiry-form" onSubmit={submit} className="bg-white border border-hairline p-8 lg:p-12 h-full">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="c-name" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Full Name *</label>
                  <input id="c-name" data-testid="contact-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" required />
                </div>
                <div>
                  <label htmlFor="c-phone" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Phone *</label>
                  <input id="c-phone" data-testid="contact-input-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 98xxx xxxxx" required />
                </div>
                <div>
                  <label htmlFor="c-email" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Email *</label>
                  <input id="c-email" data-testid="contact-input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" required />
                </div>
                <div>
                  <label htmlFor="c-property" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Property Type</label>
                  <select id="c-property" data-testid="contact-select-property-type" className={`${inputCls} select-lux`} value={form.property_type} onChange={set("property_type")}>
                    <option value="">Select…</option>
                    <option>2-BHK Apartment</option>
                    <option>3-BHK Apartment</option>
                    <option>4-BHK / Larger</option>
                    <option>Villa / Bungalow</option>
                    <option>Office / Commercial</option>
                    <option>Hospitality</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="c-sqft" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Area (sq.ft)</label>
                  <input id="c-sqft" data-testid="contact-input-sqft" type="number" min="0" className={inputCls} value={form.sqft} onChange={set("sqft")} placeholder="e.g. 2350" />
                </div>
                <div>
                  <label htmlFor="c-budget" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Estimated Budget</label>
                  <select id="c-budget" data-testid="contact-select-budget" className={`${inputCls} select-lux`} value={form.budget} onChange={set("budget")}>
                    <option value="">Select…</option>
                    <option>Under ₹25 Lakhs</option>
                    <option>₹25 – ₹45 Lakhs</option>
                    <option>₹45 Lakhs – ₹1 Crore</option>
                    <option>Above ₹1 Crore</option>
                    <option>Not sure yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="c-message" className="block text-[10px] tracking-widest2 uppercase text-taupe mb-2.5">Project Details</label>
                  <textarea id="c-message" data-testid="contact-input-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your home, timeline and what you're dreaming of…" />
                </div>
                <div className="sm:col-span-2">
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={sending}
                    className="w-full sm:w-auto bg-ink text-cream px-10 py-4 text-sm tracking-wide flex items-center justify-center gap-3 hover:bg-brass hover:text-ink transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? "Sending…" : "Send Enquiry"}
                    <Send size={15} />
                  </button>
                </div>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
